/* eslint-disable react-hooks/rules-of-hooks */
import _ from 'lodash';
import useGetScales from './useGetScales';


export const getRowContribution = (row, rowContribution, chartView) => {
  const r = rowContribution[row];
  return r.values[chartView[0] - 1] + r.values[chartView[1] - 1];
};

export const getColContribution = (col, colContribution, chartView) => {
  const c = colContribution[col];
  return c.values[chartView[0] - 1] + c.values[chartView[1] - 1];
};

export const quadrant = (node, chartView) => {
  // eslint-disable-next-line no-unused-vars
  const [getScales, coordValX, coordValY] = useGetScales(chartView);
  const x = coordValX(node);
  const y = coordValY(node);
  if (x <= 0 && y <= 0) return 'topLeft';
  if (x <= 0 && y > 0) return 'bottomLeft';
  if (x > 0 && y < 0) return 'topRight';

  return 'bottomRight';
};

export const buildNodeLabelArray = (labelArray, nodeLabelPositions, updateNodeLabelPositions) => {
  const lblPositions = [];
  const nodePositions = [];
  labelArray.forEach(lbl => {
    lblPositions.push({
      key: lbl.textId,
      value: lbl.lblY,
    });
    nodePositions.push({
      id: lbl.linkId,
      x: lbl.x,
      y: lbl.circleY,
    });
  });
  if (lblPositions.length > 0 && !_.isEqual(nodeLabelPositions, lblPositions)) {
    updateNodeLabelPositions(lblPositions);
  }
  return { lblPositions, nodePositions };
};

export const getNodeText = (node, textOverride, showLongNames, xScale, xPadding, maxX) => {
  const tText = textOverride || node.label;
  const extraPadding = xPadding + 40;

  let showLN = true;
  if (node.isPassive) {
    showLN = showLongNames.forPassive;
  } else if (node.type === 'row') {
    showLN = showLongNames.forRow;
  } else if (node.type === 'col') {
    showLN = showLongNames.forCol;
  }

  let shortName = tText.length > 20 ? `${tText.substr(0, 20)}` : tText;

  if (!showLN) {
    const elem = document.getElementById('ruler');
    let charCount = shortName.length;
    if (xScale.invert(node.x) < 0) {
      // left side text
      if (elem) {
        // loop until fits or five characters
        do {
          elem.innerHTML = `${shortName}...`;
          if (node.x - elem.offsetWidth + extraPadding < 0) {
            charCount -= 1;
            shortName = shortName.substring(0, charCount);
          }
        } while (node.x - elem.offsetWidth + extraPadding < 0 && charCount > 5);
      }
    }
    // right side text
    else if (elem) {
      // loop until fits or five characters
      const xMax = xScale(maxX);
      do {
        elem.innerHTML = `${shortName}...`;
        if (node.x + elem.offsetWidth - extraPadding > xMax) {
          charCount -= 1;
          shortName = shortName.substring(0, charCount);
        }
      } while (node.x + elem.offsetWidth - extraPadding > xMax && charCount > 5);
    }
  }

  return showLN || shortName === tText ? tText : `${_.trim(shortName)}...`;
};

export const mergeData = (cm, passiveRows, passiveCols, chartView, nodeLabelPositions) => {
  // eslint-disable-next-line no-unused-vars
  const [getScales, coordValX, coordValY] = useGetScales(chartView);

  const mData = [];
  const {
    rowPoints,
    colPoints,
    rowContribution,
    colContribution,
  } = _.cloneDeep(cm);

  const updateLblY = node => {
    const item = _.find(nodeLabelPositions, o => o.key === node.textId);
    node.lblY = item && item.value ? item.value : coordValY(node);
  };

  let maxContribution = Number.MIN_VALUE;
  let minContribution = Number.MAX_VALUE;

  const nodePositionsMatchLength =
    nodeLabelPositions.length === (rowPoints.length + colPoints.length);

  let i = 0;
  rowPoints.forEach(rp => {
    rp.id = `row_${i}`;
    rp.textId = `row_text_${i}`;
    rp.linkId = `row_line_${i}`;
    rp.isPassive = (passiveRows !== undefined) && passiveRows.includes(i);
    rp.radius = 8;
    rp.type = 'row';
    rp.quadrant = quadrant(rp, chartView);
    rp.contribution = getRowContribution(i, rowContribution, chartView);
    maxContribution = (rp.isPassive === false)
      ? Math.max(rp.contribution, maxContribution)
      : maxContribution;
    minContribution = (rp.isPassive === false)
      ? Math.min(rp.contribution, minContribution)
      : minContribution;
    i += 1;
    updateLblY(rp);
    mData.push(rp);
  });

  i = 0;
  colPoints.forEach(cp => {
    cp.id = `col_${i}`;
    cp.textId = `col_text_${i}`;
    cp.linkId = `col_line_${i}`;
    cp.isPassive = (passiveCols !== undefined) && passiveCols.includes(i);
    cp.radius = 8;
    cp.type = 'col';
    cp.quadrant = quadrant(cp, chartView);
    cp.contribution = getColContribution(i, colContribution, chartView);
    maxContribution = (cp.isPassive === false)
      ? Math.max(cp.contribution, maxContribution)
      : maxContribution;
    minContribution = (cp.isPassive === false)
      ? Math.min(cp.contribution, minContribution)
      : minContribution;

    i += 1;
    updateLblY(cp);
    mData.push(cp);
  });

  // bring passives to the top
  mData.sort((a, b) => {
    return (!a.isPassive && b.isPassive)
      ? -1
      : (a.isPassive && !b.isPassive)
        ? 1
        : Math.abs(coordValX(b)) - Math.abs(coordValX(a));
  });
  return { mData, nodePositionsMatchLength, maxContribution, minContribution };
};

// export const mergeData = (
//   cm,
//   passiveRows,
//   passiveCols,
//   chartView,
//   nodeLabelPositions
// ) => {
//   // eslint-disable-next-line no-unused-vars
//   const [getScales, coordValX, coordValY] = useGetScales(chartView);

//   const mData = [];
//   const {
//     rowPoints,
//     colPoints,
//     rowContribution,
//     colContribution,
//   } = _.cloneDeep(cm);

//   const updateLblY = node => {
//     const item = _.find(nodeLabelPositions, o => o.key === node.textId);
//     node = {
//       ...node,
//       lblY: item && item.value ? item.value : coordValY(node),
//     };
//   };

//   let maxContribution = Number.MIN_VALUE;
//   let minContribution = Number.MAX_VALUE;

//   const nodePositionsMatchLength =
//     nodeLabelPositions.length === rowPoints.length + colPoints.length;

//   let i = 0;
//   rowPoints.forEach(rp => {
//     const rowP = {
//       ...rp,
//       id: `row_${i}`,
//       textId: `row_text_${i}`,
//       linkId: `row_line_${i}`,
//       isPassive: passiveRows !== undefined && passiveRows.includes(i),
//       radius: 8,
//       type: 'row',
//       quadrant: quadrant(rp, chartView),
//       contribution: getRowContribution(i, rowContribution, chartView),
//     };
//     maxContribution =
//       rowP.isPassive === false
//         ? Math.max(rp.contribution, maxContribution)
//         : maxContribution;
//     minContribution =
//       rowP.isPassive === false
//         ? Math.min(rp.contribution, minContribution)
//         : minContribution;
//     i += 1;
//     updateLblY(rowP);
//     mData.push(rowP);
//   });

//   i = 0;
//   colPoints.forEach(cp => {
//     const colP = {
//       ...cp,
//       id: `col_${i}`,
//       textId: `col_text_${i}`,
//       linkId: `col_line_${i}`,
//       isPassive: passiveCols !== undefined && passiveCols.includes(i),
//       radius: 8,
//       type: 'col',
//       quadrant: quadrant(cp, chartView),
//       contribution: getColContribution(i, colContribution, chartView),
//     };
//     maxContribution =
//       colP.isPassive === false
//         ? Math.max(cp.contribution, maxContribution)
//         : maxContribution;
//     minContribution =
//       colP.isPassive === false
//         ? Math.min(c.contribution, minContribution)
//         : minContribution;

//     i += 1;
//     updateLblY(colP);
//     mData.push(colP);
//   });

//   // bring passives to the top
//   mData.sort((a, b) => {
//     return !a.isPassive && b.isPassive
//       ? -1
//       : a.isPassive && !b.isPassive
//       ? 1
//       : Math.abs(coordValX(b)) - Math.abs(coordValX(a));
//   });

//   return { mData, nodePositionsMatchLength, maxContribution, minContribution };
// };

